import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Drawer,
  Dropdown,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  notification,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  UploadProps,
} from "antd";
import { ItemType } from "antd/es/menu/interface";
import { ColumnsType } from "antd/es/table";
import { RcFile } from "antd/es/upload";
import { ApiService } from "api/api.service";
import CreditorPaymentMethodModal from "components/CreditorPaymentMethodModal";
import CustomModal from "components/CustomStrategy/CustomModal";
import PageTitle from "components/PageTitle";
import TextNumeric from "components/TextNumeric";
import dayjs from "dayjs";
import { APPLICATION_STATUS, ApplicationSchemaDto } from "dtos/application.dto";
import { CreditorDto } from "dtos/creditor.dto";
import { BoardResource } from "dtos/kanban.dto";
import { RoleTypes } from "dtos/user.dto";
import { WalletTypes } from "dtos/wallet.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useEffect, useMemo, useState } from "react";
import { LuListFilter } from "react-icons/lu";
import {
  TbAffiliate,
  TbArchive,
  TbArrowsExchange,
  TbArrowsUpDown,
  TbChevronLeft,
  TbChevronRight,
  TbClipboardCheck,
  TbColumns3,
  TbCopy,
  TbDots,
  TbEdit,
  TbEye,
  TbEyeDollar,
  TbGitMerge,
  TbInfoCircle,
  TbLayoutKanban,
  TbLoader2,
  TbPaywall,
  TbReload,
  TbSearch,
  TbSortAscendingNumbers,
  TbSortDescendingNumbers,
  TbTransactionDollar,
  TbX,
} from "react-icons/tb";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppSelector } from "store";
import { formatDateTime, formatMoney, fuzzyMatch } from "utils/functions";
import { queryClient } from "utils/queryClient";

export type PopulatedCreditorDto = NonNullable<
  ReturnType<typeof ApiService.creditor.useGetPaginated>["data"]
>["result"]["data"][number];

type T_Params = Parameters<typeof ApiService.creditor.useGetPaginated>[0];

const tagEllipsisStyle: React.CSSProperties = {
  maxWidth: 120,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "inline-block",
  verticalAlign: "middle",
};

function CreditorsAll() {
  const currentUser = useAppSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = parseInt(searchParams.get("page") || "1") ?? 1;
  const limit = parseInt(searchParams.get("limit") || "20") ?? 20;
  const search = searchParams.get("search") ?? "";
  const [showArchived, setShowArchived] = useState(false);
  const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCreditor, setSelectedCreditor] = useState<CreditorDto | undefined>();
  const [params, setParams] = useState<T_Params>({ page, limit, search: search, inDraft: showArchived });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const creditors = ApiService.creditor.useGetPaginated(params);
  const addCreditor = ApiService.creditor.useCreateCreditor();
  const updateCreditor = ApiService.creditor.useUpdateCreditor();
  const mergeCreditor = ApiService.creditor.useMergeCreditor();
  const allCreditors = ApiService.creditor.useGetAllCreditorNames();
  const parseCSV = ApiService.creditor.useParseCSV();
  const allCreditorsData = allCreditors.data?.result ?? [];
  const [openMergeCreditor, setOpenMergeCreditor] = useState(false);
  const [mergeCreditorForm] = Form.useForm();
  const loanPipeline = ApiService.kanban.useGetBoard({
    resource: BoardResource.LOAN,
    scope: BoardResource.CREDITOR,
  });

  const sortOrder = params?.sort && Object.values(params?.sort)?.length > 0 ? Object.values(params?.sort)[0] : -1;

  const renderArrayWithTooltip = (values: string[] = []) => {
    if (!values.length) return "-";

    const visible = values.slice(0, 2);
    const remaining = values.length - visible.length;

    return (
      <Tooltip
        title={
          <div className="max-w-[300px]">
            {values.map((v) => (
              <div key={v} className="whitespace-nowrap">
                {v}
              </div>
            ))}
          </div>
        }
        styles={{ root: { fontSize: 13 } }}
      >
        <Space size={4} wrap>
          {visible.map((v) => (
            <Tag key={v}>
              <span style={tagEllipsisStyle}>{v}</span>
            </Tag>
          ))}

          {remaining > 0 && <Tag color="default">+{remaining}</Tag>}
        </Space>
      </Tooltip>
    );
  };

  const renderBoolean = (value?: boolean) => (value === true ? "Yes" : value === false ? "No" : "-");

  const renderTextWithTooltip = (value?: string) => {
    if (!value) return "-";
    return (
      <Tooltip title={value}>
        <Typography.Text ellipsis style={{ maxWidth: 200 }}>
          {value}
        </Typography.Text>
      </Tooltip>
    );
  };

  const renderNumber = (value?: number) => value ?? "-";

  const columns: ColumnsType<PopulatedCreditorDto> = [
    {
      key: "creditor.name",
      title: "Name",
      dataIndex: "name",
      width: 260,
      fixed: "left",
      render: (val, record) => {
        return (
          <div className="flex items-center gap-2">
            <Typography>{val}</Typography>
            {record.isPartner && <Tag color="green">PARTNER</Tag>}
          </div>
        );
      },
    },
    {
      key: "creditor.alternate_names",
      title: "Alternate Names",
      dataIndex: "alternate_names",
      render: renderArrayWithTooltip,
      width: 180,
    },
    {
      key: "creditor.allowed_emails",
      title: "Allowed Emails",
      dataIndex: "allowed_emails",
      render: renderArrayWithTooltip,
      width: 180,
    },
    {
      key: "creditor.allowed_phones",
      title: "Allowed Phones",
      dataIndex: "allowed_phones",
      render: renderArrayWithTooltip,
      width: 180,
    },
    {
      key: "creditor.allowed_domains",
      title: "Allowed Domains",
      dataIndex: "allowed_domains",
      render: renderArrayWithTooltip,
      width: 180,
    },
    {
      key: "creditor.statement_short_code",
      title: "Statement Short Code",
      dataIndex: "statement_short_code",
      render: renderTextWithTooltip,
      width: 150,
    },
    {
      key: "creditor.paper_type",
      title: "Paper Type",
      dataIndex: "paper_type",
      render: renderTextWithTooltip,
      width: 150,
    },
    {
      key: "creditor.positions",
      title: "Positions",
      dataIndex: "positions",
      render: renderTextWithTooltip,
      width: 150,
    },
    {
      key: "creditor.hard_credit_pull",
      title: "Hard Credit Pull?",
      dataIndex: "hard_credit_pull",
      render: renderBoolean,
      width: 150,
    },
    {
      key: "creditor.lawyers",
      title: "Lawyers?",
      dataIndex: "lawyers",
      render: renderBoolean,
      width: 120,
    },
    {
      key: "creditor.defaults",
      title: "Defaults?",
      dataIndex: "defaults",
      render: renderBoolean,
      width: 120,
    },
    {
      key: "creditor.shortest_term_days",
      title: "Shortest Term (Days)",
      dataIndex: "shortest_term_days",
      render: renderNumber,
      width: 180,
    },
    {
      key: "creditor.longest_term_days",
      title: "Longest Term (Days)",
      dataIndex: "longest_term_days",
      render: renderNumber,
      width: 180,
    },
    {
      key: "creditor.lowest_buy_rate",
      title: "Lowest Buy Rate",
      dataIndex: "lowest_buy_rate",
      render: renderNumber,
      width: 150,
    },
    {
      key: "creditor.min_amount_offered",
      title: "Min Amount Offered",
      dataIndex: "min_amount_offered",
      render: renderNumber,
      width: 170,
    },
    {
      key: "creditor.max_amount_offered",
      title: "Max Amount Offered",
      dataIndex: "max_amount_offered",
      render: renderNumber,
      width: 170,
    },
    {
      key: "creditor.min_credit_score",
      title: "Min Credit Score",
      dataIndex: "min_credit_score",
      render: renderNumber,
      width: 150,
    },
    {
      key: "creditor.min_monthly_revenue",
      title: "Min Monthly Revenue",
      dataIndex: "min_monthly_revenue",
      render: renderNumber,
      width: 180,
    },
    {
      key: "creditor.neg_days_three_months",
      title: "Negative Days (3 Months)",
      dataIndex: "neg_days_three_months",
      render: renderNumber,
      width: 180,
    },
    {
      key: "creditor.bio",
      title: "Bio",
      dataIndex: "bio",
      render: renderTextWithTooltip,
      width: 200,
    },
    {
      key: "creditor.key_infos",
      title: "Key Infos",
      dataIndex: "key_infos",
      render: renderTextWithTooltip,
      width: 200,
    },
    {
      key: "createdAt",
      dataIndex: "createdAt",
      title: "Created at",
      width: 200,
      render: (_, record) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{record?.createdAt ? formatDateTime(record?.createdAt) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => {
        const menuItems: ItemType[] = [
          {
            key: "edit-creditor",
            icon: <TbEdit />,
            label: "Edit Details",
            onClick: () => {
              setSelectedCreditor(record);
              setIsOpen((prev) => !prev);
            },
          },
          ...(!record.inDraft
            ? []
            : [
                {
                  key: "move-from-draft",
                  icon: <TbArchive />,
                  label: "Remove from Draft",
                  onClick: () => {
                    updateCreditor.mutate(
                      { slug: record.slug, dto: { inDraft: false } },
                      {
                        onSuccess: () => {
                          message.success(`Creditor has been moved from draft.`);
                          creditors.refetch();
                        },
                      },
                    );
                  },
                },
              ]),
          {
            key: "merge-creditor",
            icon: <TbGitMerge />,
            label: "Merge Creditor",
            onClick: () => {
              setSelectedCreditor(record);
              setOpenMergeCreditor(true);
            },
          },
          {
            key: "register-payment-method",
            icon: <TbPaywall />,
            label: "Add Payment Methods",
            onClick: () => {
              setSelectedCreditor(record);
              setOpenPaymentMethodModal(true);
            },
          },
          {
            key: "navigate-creditor-logs",
            icon: <TbTransactionDollar />,
            label: "View Creditor Txn. Logs",
            onClick: () => {
              if (record._id) {
                window.open(
                  `/dashboard/transaction-logs?creditor_id=${record._id}&wallet_type=${WalletTypes.CREDITOR}`,
                );
              } else {
                message.error("Creditor ID not found.");
              }
            },
          },
        ];

        if (loanPipeline.data?._id.length) {
          menuItems.push({
            key: "view-loans-pipeline",
            icon: <TbLayoutKanban />,
            label: "View Loans Pipeline",
            onClick() {
              const pipelineId = loanPipeline.data?._id;
              if (pipelineId) navigate(`/dashboard/operations/pipelines/${pipelineId}?scopeId=${record._id}`);
            },
          });
        }

        return (
          <>
            <Dropdown
              menu={{
                items: menuItems,
              }}
            >
              <Button icon={<TbDots />} />
            </Dropdown>
          </>
        );
      },
    },
  ];

  const [form] = Form.useForm();

  useEffect(() => {
    if (isOpen) {
      if (selectedCreditor) {
        form.setFieldsValue(selectedCreditor);
      } else {
        form.resetFields();
      }
    }
  }, [isOpen, selectedCreditor, form]);

  useEffect(() => {
    if (selectedCreditor) {
      form.setFieldsValue({
        ...selectedCreditor,
        isPartner: selectedCreditor.isPartner ?? false,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ isPartner: false });
    }
  }, [selectedCreditor]);

  const uploadProps: UploadProps = {
    accept: ".csv",
    maxCount: 1,
    showUploadList: false,
    beforeUpload: () => false,
  };

  return (
    <DashboardLayout
      headerRight={
        <div className="flex gap-2 items-center">
          {currentUser.role === RoleTypes.SUPERADMIN ? (
            <Upload
              {...uploadProps}
              onChange={async ({ file }) => {
                const formData = new FormData();
                formData.append("file", file as RcFile);

                parseCSV.mutate(formData, {
                  onSuccess: (data) => {
                    message.success(`CSV has been processed and uploaded.`);
                    queryClient.invalidateQueries({ queryKey: ["creditors"] });
                    if (data.result.skippedRows.length) {
                      notification.info({
                        message: `There were certain entries for which similar matches were found. The skipped rows are: ${data.result.skippedRows.join(", ")}.`,
                        placement: "bottomRight",
                        duration: 0,
                      });
                    }
                  },
                });
              }}
            >
              <Button variant="solid" color="green" icon={<UploadOutlined />} loading={parseCSV.isPending}>
                Parse from CSV
              </Button>
            </Upload>
          ) : null}

          <Button
            type="primary"
            onClick={() => {
              setSelectedCreditor(undefined);
              setIsOpen(true);
            }}
          >
            Add Creditor
          </Button>
        </div>
      }
    >
      {openPaymentMethodModal && (
        <CreditorPaymentMethodModal
          creditor_id={selectedCreditor?._id ?? ""}
          open={openPaymentMethodModal}
          onClose={() => {
            setSelectedCreditor(undefined);
            setOpenPaymentMethodModal(false);
          }}
        />
      )}
      <Modal
        open={isOpen}
        title={selectedCreditor ? "Edit Creditor" : "Create Creditor"}
        onCancel={() => {
          setIsOpen(false);
          setSelectedCreditor(undefined);
          form.resetFields();
        }}
        footer={null}
        width={800}
        centered
        styles={{
          body: {
            height: 600,
            overflowY: "auto",
          },
          content: {
            padding: 24,
          },
        }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ isPartner: false }}
          onFinish={(values) => {
            if (selectedCreditor) {
              const { slug, ...rest } = values;
              updateCreditor.mutate(
                { slug, dto: rest },
                {
                  onSuccess: () => {
                    message.success("Creditor updated succesfully!");
                    queryClient.invalidateQueries({ queryKey: ["creditors"] });
                  },
                },
              );
            } else {
              addCreditor.mutate(
                { dto: values },
                {
                  onSuccess: () => {
                    message.success("Creditor added succesfully!");
                    queryClient.invalidateQueries({ queryKey: ["creditors"] });
                    allCreditors.refetch();
                  },
                },
              );
            }

            setIsOpen(false);
          }}
        >
          <div className="grid grid-cols-2 gap-x-4">
            <Divider className="col-span-2 !my-2">Basic Information</Divider>
            <Form.Item label="Name" name="name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item label="Statement Short Code" name="statement_short_code">
              <Input />
            </Form.Item>

            <Form.Item name="isPartner" label="Is a partner?" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>

            <Form.Item
              label="Allowed Emails"
              name="allowed_emails"
              className="col-span-2"
              rules={[
                {
                  validator: (_, values: string[] = []) => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const invalid = values.filter((v) => !emailRegex.test(v));

                    if (invalid.length) {
                      return Promise.reject(new Error(`Invalid email(s): ${invalid.join(", ")}`));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select mode="tags" placeholder="Add emails" />
            </Form.Item>

            <Form.Item
              label="Allowed Domains"
              name="allowed_domains"
              className="col-span-2"
              rules={[
                {
                  validator: (_, values: string[] = []) => {
                    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

                    const invalid = values.filter((v) => !domainRegex.test(v.trim()));

                    if (invalid.length) {
                      return Promise.reject(new Error(`Invalid domain(s): ${invalid.join(", ")}`));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select mode="tags" placeholder="Add domains" />
            </Form.Item>

            <Form.Item name="slug" hidden preserve>
              <Input />
            </Form.Item>

            <Form.Item
              label="Allowed Phones"
              name="allowed_phones"
              className="col-span-2"
              rules={[
                {
                  validator: (_, values: string[] = []) => {
                    const e164Regex = /^\+[1-9]\d{1,14}$/;
                    const invalid = values.filter((v) => !e164Regex.test(v));

                    if (invalid.length) {
                      return Promise.reject(
                        new Error(
                          `Invalid phone number(s): ${invalid.join(", ")}. Use international format, e.g. +14155552671`,
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select mode="tags" placeholder="Add phone numbers" />
            </Form.Item>

            <Form.Item label="Alternate Names" name="alternate_names" className="col-span-2">
              <Select mode="tags" placeholder="Add alternate names" />
            </Form.Item>

            <Divider className="col-span-2 !my-2">Payment Information</Divider>

            <Form.Item label="Email" name={["payment_details", "email"]}>
              <Input placeholder="Email of registered Paynote account" />
            </Form.Item>

            <Form.Item label="Phone" name={["payment_details", "phone"]}>
              <InputNumber<string>
                type="tel"
                minLength={14}
                maxLength={14}
                style={{ width: "100%" }}
                formatter={(value) => {
                  if (!value) return "";
                  const onlyNumbers = `${value}`.replace(/\D/g, "");
                  const formatted = onlyNumbers.replace(
                    /^(\d{0,3})(\d{0,3})(\d{0,4})$/,
                    (match, p1, p2, p3) => `${p1 ? `(${p1}) ` : ""}${p2 ? `${p2}` : ""}${p3 ? `-${p3}` : ""}`,
                  );
                  return formatted.trim();
                }}
                parser={(value) => value?.replace(/\D/g, "") as unknown as string}
                controls={false}
              />
            </Form.Item>

            <Form.Item label="First Name" name={["payment_details", "first_name"]}>
              <Input />
            </Form.Item>

            <Form.Item label="Last Name" name={["payment_details", "last_name"]}>
              <Input />
            </Form.Item>

            <Divider className="col-span-2 !my-2">Address Information</Divider>

            <Form.Item label="Street Address" name={["address_info", "street_address"]}>
              <Input />
            </Form.Item>

            <Form.Item label="City" name={["address_info", "city"]}>
              <Input />
            </Form.Item>

            <Form.Item label="State" name={["address_info", "state"]}>
              <Input />
            </Form.Item>

            <Form.Item label="Zip Code" name={["address_info", "zip_code"]}>
              <Input />
            </Form.Item>

            <Divider className="col-span-2 !my-2">Dealing Information</Divider>

            <Form.Item label="Paper Type" name="paper_type">
              <Input />
            </Form.Item>

            <Form.Item label="Positions" name="positions">
              <Input />
            </Form.Item>

            <Form.Item label="Bio" name="bio" className="col-span-2">
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item label="Key Infos" name="key_infos" className="col-span-2">
              <Input.TextArea rows={3} />
            </Form.Item>

            <Divider className="col-span-2 !my-2">Dealing Ranges</Divider>

            <Form.Item label="Shortest Term (Days)" name="shortest_term_days">
              <InputNumber className="w-full" min={0} />
            </Form.Item>

            <Form.Item label="Longest Term (Days)" name="longest_term_days">
              <InputNumber className="w-full" min={0} />
            </Form.Item>

            <Form.Item label="Lowest Buy Rate" name="lowest_buy_rate">
              <InputNumber className="w-full" min={0} step={0.01} />
            </Form.Item>

            <Form.Item label="Min Credit Score" name="min_credit_score">
              <InputNumber className="w-full" min={0} max={850} />
            </Form.Item>

            <Form.Item label="Min Amount Offered" name="min_amount_offered">
              <InputNumber className="w-full" min={0} prefix="$" />
            </Form.Item>

            <Form.Item label="Max Amount Offered" name="max_amount_offered">
              <InputNumber className="w-full" min={0} prefix="$" />
            </Form.Item>

            <Form.Item label="Min Monthly Revenue" name="min_monthly_revenue">
              <InputNumber className="w-full" min={0} prefix="$" />
            </Form.Item>

            <Form.Item label="Neg Days (3 Months)" name="neg_days_three_months">
              <InputNumber className="w-full" min={0} />
            </Form.Item>

            <Divider className="col-span-2 !my-2">Flags</Divider>

            <Form.Item label="Hard Credit Pull" name="hard_credit_pull" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label="Lawyers" name="lawyers" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label="Defaults" name="defaults" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>

          <Form.Item className="!mb-0">
            <div className="flex justify-end gap-2">
              <Button onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {selectedCreditor ? "Update" : "Create"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <PageTitle pageTitle={[{ title: "Creditors" }]} title="Creditors" />
      <div className="flex justify-between gap-3 p-3">
        <div className="inline-flex gap-3 items-center">
          <Space.Compact>
            <Dropdown
              trigger={["click"]}
              menu={{
                selectable: true,
                selectedKeys: params?.sort ? Object.keys(params.sort) : [],
                onSelect: ({ key }) => setParams((prev) => ({ ...prev, sort: { [key]: sortOrder } as any })),
                items: [
                  // {
                  //   key: "name",
                  //   label: "Name",
                  // },
                  {
                    key: "createdAt",
                    label: "Created at",
                  },
                  // {
                  //   key: "lead.debt_amount",
                  //   label: "Debt Amount",
                  // },
                ],
              }}
            >
              <Button
                className="border-gray-300"
                variant={params?.sort ? "filled" : "outlined"}
                color={params?.sort ? "primary" : "default"}
                icon={<TbArrowsUpDown />}
              >
                Sort
              </Button>
            </Dropdown>
            {params?.sort && Object.keys(params?.sort)?.length > 0 ? (
              <>
                <Button
                  color="default"
                  className="border-gray-300 px-2"
                  icon={sortOrder === -1 ? <TbSortDescendingNumbers /> : <TbSortAscendingNumbers />}
                  onClick={() =>
                    setParams((prev) => {
                      if (!prev?.sort) return prev;
                      const key = Object.keys(prev?.sort)?.[0];
                      return { ...prev, sort: { [key]: sortOrder === 1 ? -1 : 1 } };
                    })
                  }
                >
                  <Typography.Text className="text-xs w-10">{sortOrder === -1 ? "DESC" : "ASC"}</Typography.Text>
                </Button>
                <Button icon={<TbX />} onClick={() => setParams((prev) => ({ ...prev, sort: undefined }))} />
              </>
            ) : null}
          </Space.Compact>
          <Space.Compact>
            <Button
              icon={<TbArchive />}
              type={!showArchived ? "default" : "primary"}
              onClick={() => {
                setShowArchived((prev) => !prev);
                setParams((prev) => ({ ...prev, inDraft: !params.inDraft }));
              }}
            >
              {!showArchived ? "Show Draft" : "Hide Draft"}
            </Button>
          </Space.Compact>
        </div>
        <div className="inline-flex gap-3 items-center">
          <Input
            prefix={<TbSearch />}
            defaultValue={search}
            placeholder="Search creditors..."
            onPressEnter={(e) => {
              setParams((prev) => ({ ...prev, search: e.currentTarget.value, page: 1 }));
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("search", e.currentTarget.value);
                newParams.set("page", "1");
                return newParams;
              });
            }}
            allowClear
            onClear={() => {
              setParams((prev) => ({ ...prev, search: undefined, page: 1 }));
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("page", "1");
                newParams.delete("search");
                return newParams;
              });
            }}
          />
        </div>
      </div>
      <Table<PopulatedCreditorDto>
        scroll={{ x: "3000px", y: "calc(100vh - 202px)" }}
        virtual={false}
        size="small"
        columns={columns}
        loading={{
          spinning: creditors.isFetching,
          indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
        }}
        dataSource={creditors.data?.result.data}
        rowKey={"_id"}
        pagination={{
          hideOnSinglePage: false,
          showQuickJumper: true,
          showPrevNextJumpers: true,
          nextIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronRight size={20} />}
            />
          ),
          prevIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronLeft size={20} />}
            />
          ),
          showTitle: true,
          showTotal: (total, range) => {
            return `Showing ${range[0]}-${range[1]} of ${total} creditors`;
          },
          showSizeChanger: true,
          total: creditors.data?.result?.totalObjects,
          current: params.page,
          pageSize: params.limit,
          onChange: (page, pageSize) => {
            setParams({ page: page, limit: pageSize, search: search, inDraft: showArchived });
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set("page", page.toString());
              newParams.set("limit", pageSize.toString());
              if (search) {
                newParams.set("search", search.toString());
              }
              return newParams;
            });
          },
        }}
      />
      <Modal
        title="Merge to existing creditor"
        open={openMergeCreditor}
        onCancel={() => {
          mergeCreditorForm.resetFields();
          setSelectedCreditor(undefined);
          setOpenMergeCreditor(false);
        }}
        width={520}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              mergeCreditorForm.resetFields();
              setSelectedCreditor(undefined);
              setOpenMergeCreditor(false);
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => mergeCreditorForm.submit()}>
            Merge Creditor
          </Button>,
        ]}
      >
        <Form
          form={mergeCreditorForm}
          layout="vertical"
          onFinish={(values) => {
            mergeCreditor.mutate(
              {
                old_creditor_id: selectedCreditor?._id ?? "",
                new_creditor_id: values.creditor_id,
              },
              {
                onSuccess: () => {
                  message.success("Creditor merged successfully.");
                  creditors.refetch();
                  setSelectedCreditor(undefined);
                  setOpenMergeCreditor(false);
                },
              },
            );
          }}
        >
          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                Creditor to be merged
              </div>
              <div className="text-base font-semibold text-gray-900">{selectedCreditor?.name || "-"}</div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-500">POTENTIAL MATCHES</div>
                <Button size="small" type="primary" icon={<TbReload />} onClick={() => allCreditors.refetch()}>
                  Refetch
                </Button>
              </div>

              <div className="text-sm text-gray-800 leading-relaxed">
                {(() => {
                  const matches = fuzzyMatch(
                    allCreditorsData.map((c) => c.name),
                    selectedCreditor?.name ?? "",
                  )
                    ?.filter((name) => name !== selectedCreditor?.name)
                    ?.slice(0, 5);

                  if (!matches || matches.length === 0) {
                    return <span className="text-gray-400">No close matches found</span>;
                  }

                  return matches.map((name, index) => {
                    const creditor = allCreditorsData.find((c) => c.name === name);

                    if (!creditor) return null;

                    const isAutoSelected = mergeCreditorForm.getFieldValue("creditor_id") === creditor._id;

                    return (
                      <div
                        key={name}
                        className={`flex items-center justify-between gap-2 py-1 ${
                          isAutoSelected ? "bg-purple-50 rounded px-2" : ""
                        }`}
                      >
                        <span className="font-medium truncate">{name}</span>

                        <TbArrowsExchange
                          size={14}
                          className="cursor-pointer text-purple-600 hover:text-purple-800"
                          onClick={() => {
                            mergeCreditorForm.setFieldsValue({
                              creditor_id: creditor._id,
                            });

                            message.success(`Selected "${creditor.name}"`);
                          }}
                        />
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <Form.Item
              name="creditor_id"
              label="Merge into creditor"
              rules={[{ required: true, message: "Select a creditor" }]}
            >
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder="Search and select creditor"
                options={allCreditorsData
                  .filter((c) => c._id !== selectedCreditor?._id)
                  .map((c) => ({
                    label: c.name,
                    value: c._id,
                  }))}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </DashboardLayout>
  );
}

export default CreditorsAll;
